import { Icon } from '@components/common/generic-icon';
import moment from 'moment';
import { Amount, RoundingDirection, DAY_MONTH_FORMAT, formatAmount } from '@model/common';
import { StarRating } from '@components/common/star-rating';
import React from 'react';
import styled from '@emotion/styled';
import { boxShadowMixins, mixins } from '@styles/mixins';
import { css } from '@emotion/css';
import { OtherPrice, Result } from '@model/iceberg/deal-finder/deal-finder';
import { ThemeProps } from '@theme/base';
import { getCrossfadeImages } from '@util/product';
import { HeroGallery } from '@components/common/hero-gallery/HeroGallery';
import { Separator } from '@components/common/separator';
import { useI18NextContext } from '@components/hooks';
import { ToolTip } from '@components/common/tooltip';
import { ToolTipContent } from '@components/common/deal-finder-tile/tooltip/ToolTipContent';
import { Icons } from '@model/common/icons';
import { mq } from '@styles/breakpoints';
import { TestId } from '@components/test-ids';
import { SkeletonElement, SkeletonInlineWrapper, SkeletonVariant } from '@components/common/skeleton-loading';
import { RippleCard } from '@components/common/ripple-card';
import { TileDiscountBanner } from '@styles/common';

interface ContainerProps extends ThemeProps {
  isClickable: boolean;
}
export const Container: any = styled.div(({ theme, isClickable }: ContainerProps) => ({
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).default,
  borderRadius: theme.custom.spacing.small,
  overflow: 'hidden',
  position: 'relative',
  height: '100%',
  color: theme.custom.colors.group4.base,
  cursor: isClickable ? 'pointer' : 'default',
  ...(isClickable && {
    ':hover': {
      boxShadow: boxShadowMixins(theme.custom.colors.group10.base).heavy
    }
  })
}));

export const ContentContainer: any = styled.div({});

const IntroContainer = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium
}));

const PricingContainer = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  [PriceRow]: {
    marginBottom: theme.custom.spacing.small,
    '&:last-of-type': {
      marginBottom: 0
    }
  }
}));

export const DestinationLabel = styled.span(({ theme }: ThemeProps) => ({
  display: 'block',
  textTransform: 'uppercase',
  ...(theme.custom.typography.labels.small as any),
  fontWeight: 500,
  ...mixins(theme).truncate
}));

export const HotelNameLabel = styled.span(({ theme }: ThemeProps) => ({
  display: 'block',
  marginTop: theme.custom.spacing.xSmall,
  ...(theme.custom.typography.h5 as any),
  fontWeight: 500,
  ...mixins(theme).truncate
}));

export const PriceLabelDefault = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any)
}));

export const AttributesContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap'
});

interface AttributeProps extends ThemeProps {
  noDisplay?: boolean;
}
export const Attribute: any = styled.div(({ theme, noDisplay }: AttributeProps) => ({
  paddingRight: theme.custom.spacing.small,
  marginTop: theme.custom.spacing.xSmall,
  display: noDisplay ? 'none' : 'block',
  ['&:last-of-type']: {
    paddingRight: 0
  }
}));

export const AttributeLabel = styled.span(({ theme }: ThemeProps) => ({
  paddingLeft: 3,
  whiteSpace: 'nowrap',
  ...(theme.custom.typography.labels.default as any),
  color: theme.custom.colors.group4.light
}));

export const RatingContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginTop: 10,
  justifyContent: 'flex-start'
});

export const tripAdvisorRating = css({
  maxWidth: '40%'
});

export const PerPerson = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any)
}));

const PriceRow: any = styled.div(({ noDisplay }: { noDisplay?: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  visibility: noDisplay ? 'hidden' : 'visible'
}));

const MealPlan = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  ...mixins(theme).truncate
}));

const Price = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  margin: 0,
  display: 'inline-block',
  marginRight: 2
}));

const BoldPrice = styled.h5(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  fontWeight: 'bold',
  margin: 0,
  display: 'inline-block',
  marginRight: 2
}));

const UpperCase = styled.span({
  textTransform: 'uppercase'
});

const PriceContainer = styled.div({
  whiteSpace: 'nowrap'
});

const ViewMore = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  ...mixins(theme).link,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  ['> span']: {
    textDecoration: 'underline'
  }
}));

const MoreInfoIcon: any = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.4rem',
  ...mixins(theme).link,
  textDecoration: 'none',
  marginLeft: theme.custom.spacing.xSmall
}));

const AttributeIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.4rem',
  color: theme.custom.colors.group4.base
}));

const StyledToolTip = styled(ToolTip)({
  [mq.small]: {
    '.react-tooltip-lite': {
      width: 350
    }
  }
});
//

export interface DealFinderTileProps {
  result: Result;
  onClick: (payload: Array<string>) => void;
  isGhosting?: boolean;
  hotelOnlySearch?: boolean;
}

export const DealFinderTile = ({ result, onClick, isGhosting, hotelOnlySearch }: DealFinderTileProps) => {
  const t: any = useI18NextContext();
  const showViewMore: boolean = result.otherPrices?.length > 2;
  const moreOptions: number = (result.otherPrices?.length || 0) - 2;

  const { summary, leadInPrice, otherPrices, hotelOnly } = result;
  const { mealPlan, perPerson } = leadInPrice || {};
  const { featuredImage, gallery, place, hotelRating, nights, startDate, tripAdvisorRating } = summary || {};
  const { resort, region, hotel } = place || {};
  const hotelOnlyAllowed = !hotelOnlySearch || hotelOnly;

  const handleOnClick = () => onClick(result.leadInPrice.packageReferences);
  if (isGhosting) {
    return (
      <Container data-testid={TestId.dealFinder.tile.container} key={place.hotel?.path}>
        <div>
          <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={200} />
        </div>
        <ContentContainer>
          <IntroContainer>
            <DestinationLabel>
              <SkeletonElement variant={SkeletonVariant.TEXT} width={'20%'} />
            </DestinationLabel>
            <HotelNameLabel>
              <SkeletonElement variant={SkeletonVariant.TEXT} width={'40%'} />
            </HotelNameLabel>
            <SkeletonInlineWrapper>
              <SkeletonElement variant={SkeletonVariant.TEXT} width={100} inline />
              <SkeletonElement variant={SkeletonVariant.TEXT} width={100} inline />
            </SkeletonInlineWrapper>
          </IntroContainer>
          <Separator />
          <PricingContainer>
            {[...Array(3)].map((_, index) => (
              <SkeletonInlineWrapper key={`skeleton-${index}`}>
                <SkeletonElement variant={SkeletonVariant.TEXT} width={'40%'} />
                <SkeletonElement variant={SkeletonVariant.TEXT} width={'20%'} right />
              </SkeletonInlineWrapper>
            ))}
          </PricingContainer>
        </ContentContainer>
      </Container>
    );
  }

  return (
    <RippleCard disabled={!hotelOnlyAllowed}>
      <Container
        data-testid={TestId.dealFinder.tile.container}
        onClick={hotelOnlyAllowed ? handleOnClick : null}
        isClickable={hotelOnlyAllowed}
      >
        {perPerson.discount && perPerson?.discountCode && (
          <TileDiscountBanner>
            {t('deal-finder__tile--discount-price-label', {
              discount: formatAmount(perPerson.discount, perPerson?.currency),
              code: perPerson.discountCode
            })}
          </TileDiscountBanner>
        )}
        <HeroGallery images={getCrossfadeImages(featuredImage, gallery)} isTile />
        <ContentContainer data-testid={TestId.dealFinder.tile.contentContainer}>
          <IntroContainer>
            <DestinationLabel data-testid={TestId.dealFinder.tile.destination}>
              {resort?.display}, {region?.display}
            </DestinationLabel>
            <HotelNameLabel data-testid={TestId.dealFinder.tile.hotel}>{hotel?.display}</HotelNameLabel>
            <AttributesContainer>
              <Attribute data-testid={TestId.dealFinder.tile.startDateAttribute} noDisplay={!startDate}>
                <AttributeIcon name={Icons.CALENDAR} />
                <AttributeLabel data-testid={TestId.dealFinder.tile.startDate}>
                  {moment(startDate).format(DAY_MONTH_FORMAT)}
                </AttributeLabel>
              </Attribute>
              {nights && (
                <Attribute>
                  <AttributeIcon name={Icons.NIGHTS} />
                  <AttributeLabel data-testid={TestId.dealFinder.tile.nights}>
                    {nights} {t('common__label--nights')}
                  </AttributeLabel>
                </Attribute>
              )}
              {!hotelOnlySearch && (
                <Attribute>
                  <AttributeIcon name={Icons.DEPARTURE} />
                  <AttributeLabel data-testid={TestId.dealFinder.tile.flights}>
                    {t('common__label--return-flights')}
                  </AttributeLabel>
                </Attribute>
              )}
            </AttributesContainer>
            <RatingContainer>
              <StarRating rating={hotelRating} />
              {tripAdvisorRating && (
                <img className={tripAdvisorRating} src={tripAdvisorRating} alt={'TripAdvisor rating'} />
              )}
            </RatingContainer>
          </IntroContainer>
          <Separator />
          <PricingContainer>
            <PriceRow data-testid={TestId.dealFinder.tile.priceRow} noDisplay={!perPerson?.amount}>
              <MealPlan data-testid={TestId.dealFinder.tile.mealPlan}>
                {mealPlan} {t('common__label--from')}
              </MealPlan>
              <PriceContainer>
                {hotelOnlyAllowed ? (
                  <>
                    <BoldPrice data-testid={TestId.dealFinder.tile.leadInPrice}>
                      {new Amount(perPerson?.amount, perPerson?.currency?.code).round(RoundingDirection.UP).format()}
                    </BoldPrice>
                    <PerPerson>{t('common__label--per-person-short')}</PerPerson>
                  </>
                ) : (
                  <BoldPrice data-testid={TestId.dealFinder.tile.noPrice}>
                    <UpperCase>
                      {perPerson?.currency?.symbol} {t('common__label--call')}
                    </UpperCase>
                  </BoldPrice>
                )}
              </PriceContainer>
            </PriceRow>
            {otherPrices?.slice(0, 2).map((other: OtherPrice, index) => (
              <PriceRow key={other.mealPlan || index}>
                <MealPlan>
                  {other.mealPlan} {t('common__label--from')}
                </MealPlan>
                <PriceContainer>
                  {hotelOnlyAllowed ? (
                    <>
                      <Price>{formatAmount(other?.perPerson?.amount, other?.perPerson?.currency)}</Price>
                      <PerPerson>{t('common__label--per-person-short')}</PerPerson>
                    </>
                  ) : (
                    <Price>
                      <UpperCase>
                        {perPerson?.currency?.symbol} {t('common__label--call')}
                      </UpperCase>
                    </Price>
                  )}
                </PriceContainer>
              </PriceRow>
            ))}
            {showViewMore && (
              <StyledToolTip content={<ToolTipContent prices={otherPrices} />} direction={'up'} distance={10}>
                <ViewMore>
                  <span>{t('deal-finder__tile--view-more', { moreOptions })}</span>
                  <MoreInfoIcon name={Icons.INFO} />
                </ViewMore>
              </StyledToolTip>
            )}
          </PricingContainer>
        </ContentContainer>
      </Container>
    </RippleCard>
  );
};
